"use client";

import NoImage from "@/assets/images/no_image.svg";
import CldImage from "@/components/ui/cldImg/CldImage";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { cn } from "@/lib/tailwind";
import {
  generateAltText,
  combineAltTextWithImageInfo,
  IPowerList,
  isContactLensColorInStock,
} from "@/utils/product.utils";
import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import { CLink } from "../../common/customLink/CustomLink";

interface ProductColorVariantsProps {
  lang: "en" | "ar";
  selectedvariantId: string;
  variantsData: variantData[];
  isLens: boolean;
  isContactLense: boolean;
  isEyeglass: boolean;
  isOutOfStock: boolean;
  hasPowerSelected?: boolean;
  selectedVariantQuantity?: number;
  powerList?: Record<string, IPowerList[]>;
  brandName: string;
  productName: string;
}

export interface variantData {
  image: string;
  type: string;
  landingPageBaseUrl: string;
  variantId: string;
  quantityAvailable: number;
  clPerPackSize: string;
  frameColor: string;
  cliponFrameColor: string;
  color: string;
  hexcode: string | string;
  variantIds: string[];
  pairSwatchImage?: string;
}

const ProductColorVariants = ({
  lang,
  selectedvariantId,
  variantsData,
  isLens,
  isEyeglass,
  isOutOfStock,
  isContactLense,
  hasPowerSelected = false,
  selectedVariantQuantity = 0,
  powerList,
  brandName,
  productName,
}: ProductColorVariantsProps) => {
  const isSelectedColorOutOfStock = (item: variantData) => {
  if (isContactLense) {
    if (hasPowerSelected) {
      // For the currently selected color swatch — use the selected power's actual quantity
      if (item?.variantIds?.includes(selectedvariantId)) {
        return selectedVariantQuantity <= 0;
      }
      // For all other color swatches — check if ALL their powers are OOS via powerList
      if (powerList && item?.color) {
        return !isContactLensColorInStock(powerList, item.color);
      }
      return item?.quantityAvailable <= 0;
    }

    // No power selected — existing logic is correct
    if (powerList && item?.color) {
      return !isContactLensColorInStock(powerList, item.color);
    }
    return isOutOfStock || item?.quantityAvailable <= 0;
  }

  return (
    item?.quantityAvailable <= 0 ||
    (!!item?.variantIds?.includes(selectedvariantId) && isOutOfStock)
  );
};
  const isMobile = useMediaQuery("(max-width: 768px)");
  const count = 4;
  const selectedVariantIdx = variantsData?.findIndex((a) => a?.variantId === selectedvariantId);

  const [viewAll, setViewAll] = useState<boolean>(
    selectedVariantIdx + 1 > count || isMobile ? true : false,
  );
  const [colorVariants, setColorVariants] = useState(variantsData?.slice(0, count));
  const [variantRefs, setVariantRefs] = useState<Record<string, HTMLDivElement>>({});

  // hooks
  const variantRef = useRef<{ [key: string]: HTMLDivElement }>({});

  const handleClick = () => {
    setViewAll(!viewAll);
  };

  function getColorName(frameColor?: string, color?: string, cliponFrameColor?: string) {
    if (isEyeglass) {
      return frameColor || "";
    } else {
      if (frameColor && frameColor !== "") {
        return color ? `${frameColor}/${color}` : frameColor;
      } else if (cliponFrameColor) {
        return cliponFrameColor;
      } else {
        return color || "";
      }
    }
  }

  function getVariantAltText(item: variantData, index: number) {
    const extractedBrandName = brandName || "";
    const extractedProductName = productName || "";

    const variantColor = getColorName(item?.frameColor, item?.color);

    return generateAltText(
      extractedBrandName,
      extractedProductName,
      variantColor,
      "", // No swatches color for variant images
      lang,
    );
  }

  useEffect(() => {
    viewAll ? setColorVariants(variantsData) : setColorVariants(variantsData?.slice(0, count));
  }, [viewAll]);

  useEffect(() => {
    if (Object.values(variantRefs).length <= 0) return;
    if (variantRefs[selectedvariantId] !== undefined) {
      variantRef.current[selectedvariantId].scrollIntoView({
        inline: "start",
        behavior: "instant",
        block: "end",
      });
    }
  }, [selectedvariantId, Object.values(variantRefs).length]);

  useEffect(() => {
    if (Object.values(variantRef).length > 0) {
      setVariantRefs(variantRef.current);
    }
  }, []);

  return (
    <div className="w-full">
      {/* View for sunglasses/Eyeglasses */}
      {!isLens && (
        <div className="no-scrollbar my-[10px] flex w-[90vw] gap-3 max-md:overflow-x-scroll md:my-3 md:w-full md:flex-wrap">
          {colorVariants?.map((item, index) => {
            return (
              <CLink
                href={item?.landingPageBaseUrl}
                key={"variant" + index}
                className={cn(
                  "relative flex min-h-[4.5rem] min-w-[4.5rem] cursor-pointer items-center justify-center border border-white bg-tertiary-5 transition-all duration-200 ease-in-out hover:border hover:border-black lg:min-h-24 lg:min-w-24",
                  selectedvariantId === item?.variantId ? "border-black" : "",
                  item?.quantityAvailable <= 0 ? "opacity-40" : "",
                )}
                title={getColorName(item?.frameColor, item?.color)}
              >
                <CldImage
                  src={item?.image ? item?.image : NoImage.src}
                  alt={getVariantAltText(item, index)}
                  width={84}
                  height={35}
                  className="w-[3.875rem] invert-[2%] md:w-[5.25rem]"
                />
                {item?.quantityAvailable <= 0 && (
                  <div className="absolute top-0 min-h-full min-w-[2px] rotate-45 bg-tertiary-6"></div>
                )}
              </CLink>
            );
          })}
          {variantsData?.length > count && (
            <div
              className={cn(
                "min-h-20 min-w-20 cursor-pointer items-center justify-center border-black bg-tertiary-5 hover:border md:flex lg:min-h-24 lg:min-w-24",
                viewAll ? "hidden" : "flex",
              )}
              onClick={() => handleClick()}
            >
              {viewAll ? (
                <p className="px-1 text-center font-semibold">
                  Show <br /> Less
                </p>
              ) : (
                <p className="text-xl font-semibold">+{variantsData?.length - count}</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* View for colored Lenses */}
      {isLens && (colorVariants?.length ?? 0) > 0 && (
        <div className="no-scrollbar flex w-[90vw] flex-row gap-2 overflow-x-auto py-5 md:w-full md:flex-wrap md:gap-3">
          {colorVariants?.map((item, index) => {
            return (
              <div
                key={index}
                ref={(ref) => {
                  if (ref) {
                    variantRef.current[item.variantId] = ref;
                  }
                }}
              >
                <CLink
                  href={item?.landingPageBaseUrl}
                  key={"variant" + index}
                  className={cn(
                    "relative flex min-h-20 min-w-20 cursor-pointer items-center justify-center border border-white bg-tertiary-5 transition-all duration-200 ease-in-out hover:border hover:border-black lg:min-h-24 lg:min-w-24",
                    item?.variantIds?.includes(selectedvariantId) ? "border-black" : "",
                    isSelectedColorOutOfStock(item) ? "opacity-40" : "",
                  )}
                  title={getColorName(item?.frameColor, item?.color)}
                >
                  <div
                    className="h-[2.955rem] w-[2.955rem] rounded-full md:h-[3.2rem] md:w-[3.2rem]"
                    style={{ backgroundColor: item?.hexcode }}
                  ></div>
                  <Image
                    src={"/magrabi/images/pdp/cl-swatch.webp"}
                    alt={getVariantAltText(item, index)}
                    width={200}
                    height={200}
                    unoptimized
                    className="absolute h-[3rem] w-[3rem] bg-transparent md:h-[3.2rem] md:w-[3.2rem]"
                  />
                  {isSelectedColorOutOfStock(item) && (
                    <div className="absolute top-0 min-h-full min-w-[2px] rotate-45 bg-tertiary-6"></div>
                  )}
                </CLink>
              </div>
            );
          })}
          {variantsData?.length > count && (
            <div
              className={cn(
                "min-h-20 min-w-20 cursor-pointer items-center justify-center border-black bg-tertiary-5 hover:border md:flex lg:min-h-24 lg:min-w-24",
                viewAll ? "hidden" : "flex",
              )}
              onClick={() => handleClick()}
            >
              {viewAll ? (
                <p className="px-1 text-center font-semibold">
                  Show <br /> Less
                </p>
              ) : (
                <p className="text-xl font-semibold">+{variantsData?.length - count}</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProductColorVariants;